import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { QILogo } from "@/components/site/QILogo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";

const TABS = ["pending", "approved", "rejected", "partners", "waitlist"];

const PartnerRow = ({ p, onStatus, onDelete }) => (
  <div className="flex flex-col md:flex-row md:items-center gap-4 p-5 border-b border-white/5 last:border-0" data-testid={`admin-partner-row-${p.partner_id}`}>
    <div className="flex-1 min-w-0">
      <p className="font-bold truncate">{p.firm} <span className="text-[10px] font-mono text-qi-pink ml-1 uppercase">{p.profession}</span>
        <span className={`ml-2 text-[10px] font-mono uppercase ${p.status === "approved" ? "text-qi-emerald" : p.status === "rejected" ? "text-red-400" : "text-qi-gold"}`}>{p.status}</span></p>
      <p className="text-xs text-white/45">{p.name} · {p.email} · {p.city ? `${p.city}, ` : ""}{p.state} · {new Date(p.created_at).toLocaleDateString()}</p>
      <p className="text-xs text-white/60 mt-2 line-clamp-2">{p.bio}</p>
    </div>
    <div className="flex gap-2">
      {p.status !== "approved" && <button onClick={() => onStatus(p, "approved")} className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 py-2 bg-qi-emerald/15 text-qi-emerald hover:bg-qi-emerald/25" data-testid="admin-approve-partner-button"><Check size={13} />Approve</button>}
      {p.status !== "rejected" && <button onClick={() => onStatus(p, "rejected")} className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20" data-testid="admin-reject-partner-button"><X size={13} />Reject</button>}
      <button onClick={() => onDelete(p)} className="inline-flex items-center text-xs rounded-full px-3 py-2 bg-white/5 text-white/50 hover:text-red-400" data-testid="admin-delete-partner-button"><Trash2 size={13} /></button>
    </div>
  </div>
);

const Stat = ({ label, value, testid }) => (
  <div className="glass-card p-5"><p className="font-display text-3xl font-black" data-testid={testid}>{value}</p><p className="text-xs text-white/45 mt-1">{label}</p></div>
);

const join = (...xs) => xs.filter(Boolean).join(" · ");
const yn = (b) => (b ? "yes" : "no");
const detailRows = (qi) => [
  ["Contact", join(qi.contact_name, qi.title, qi.business_email, qi.phone)],
  ["Location", join([qi.city, qi.state].filter(Boolean).join(", "), qi.nationwide ? "Nationwide" : (qi.states_served || []).join(", "))],
  ["Services", (qi.services || []).join(", ")],
  ["Metros", (qi.metros_served || []).join("; ")],
  ["Credentials", join(`FEA ${yn(qi.fea_member)}`, `Bond ${qi.fidelity_bond ? `yes ${qi.bond_amount}`.trim() : "no"}`, `E&O ${yn(qi.eo_insurance)}`, `Segregated ${yn(qi.segregated_accounts)}`, (qi.certifications || []).join(", "))],
  ["Volume", join(qi.years_in_business ? `${qi.years_in_business} yrs` : "", qi.exchanges_per_year ? `${qi.exchanges_per_year}/yr` : "")],
  ["Bio", qi.bio],
  ["Goals", (qi.business_goals || []).join(", ")],
  ["Goal notes", qi.goals_notes],
  ["Ideal client", qi.ideal_client],
  ["Heard via", qi.referral_source],
].filter(([, v]) => v);

const Detail = ({ qi, onClose }) => (
  <Dialog open={!!qi} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="bg-ink-2 border-white/10 rounded-[28px] max-w-2xl text-white max-h-[85vh] overflow-y-auto" data-testid="admin-detail-dialog">
      {qi && (
        <>
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">{qi.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-white/70">
            {detailRows(qi).map(([k, v]) => (
              <p key={k} className="whitespace-pre-line"><b className="text-white">{k}:</b> {v}</p>
            ))}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const QIRow = ({ qi, onStatus, onDelete, onView }) => (
  <div className="flex flex-col md:flex-row md:items-center gap-4 p-5 border-b border-white/5 last:border-0" data-testid={`admin-row-${qi.profile_id}`}>
    <QILogo profile={qi} size="w-12 h-12" text="text-sm" />
    <button onClick={() => onView(qi)} className="flex-1 min-w-0 text-left" data-testid={`admin-view-${qi.profile_id}`}>
      <p className="font-bold truncate">{qi.company_name} {qi.is_sample && <span className="text-[10px] font-mono text-qi-gold ml-1">SAMPLE</span>}</p>
      <p className="text-xs text-white/45">{qi.contact_name} · {qi.business_email} · {qi.state} · {new Date(qi.created_at).toLocaleDateString()}</p>
    </button>
    <div className="flex gap-2">
      {qi.status !== "approved" && <button onClick={() => onStatus(qi, "approved")} className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 py-2 bg-qi-emerald/15 text-qi-emerald hover:bg-qi-emerald/25" data-testid="admin-approve-qi-button"><Check size={13} />Approve</button>}
      {qi.status !== "rejected" && <button onClick={() => onStatus(qi, "rejected")} className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20" data-testid="admin-reject-qi-button"><X size={13} />Reject</button>}
      {qi.status === "approved" && <Link to={`/qi/${qi.slug}`} className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 py-2 bg-white/5 hover:bg-white/10" data-testid="admin-open-qi-link"><ExternalLink size={13} /></Link>}
      <button onClick={() => onDelete(qi)} className="inline-flex items-center text-xs rounded-full px-3 py-2 bg-white/5 text-white/50 hover:text-red-400" data-testid="admin-delete-qi-button"><Trash2 size={13} /></button>
    </div>
  </div>
);

const LaunchCard = () => {
  const { launched, setLaunched } = useSite();
  const toggle = async () => {
    const next = !launched;
    if (!window.confirm(next ? "Open the full directory, state and city pages to the public?" : "Hide the directory again and show the founding landing page?")) return;
    try { await api.patch("/admin/site", { launched: next }); setLaunched(next); toast.success(next ? "Site launched" : "Back in pre-launch mode"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="glass-card p-6 mt-8 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="admin-launch-card">
      <div>
        <p className="font-display text-lg font-bold">Site status: <span data-testid="admin-launch-status" className={launched ? "text-qi-emerald" : "text-qi-gold"}>{launched ? "Launched" : "Pre-launch"}</span></p>
        <p className="text-sm text-white/50 mt-1">{launched ? "Investors can browse the directory, state and city pages." : "Visitors only see the founding landing page. Signed-in QIs can still browse and chat."}</p>
      </div>
      <button onClick={toggle} className={launched ? "btn-ghost" : "btn-pink"} data-testid="admin-launch-toggle-button">{launched ? "Return to pre-launch" : "Launch the directory"}</button>
    </div>
  );
};

export default function Admin() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    const req = tab === "waitlist" ? api.get("/admin/waitlist") : tab === "partners" ? api.get("/admin/partners") : api.get("/admin/qis", { params: { status: tab } });
    const [s, r] = await Promise.all([api.get("/admin/stats"), req]);
    setStats(s.data);
    setRows(r.data);
  }, [tab]);

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const onStatus = async (qi, status) => {
    try { await api.patch(`/admin/qis/${qi.profile_id}`, { status }); toast.success(`${qi.company_name} ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (qi) => {
    if (!window.confirm(`Delete ${qi.company_name}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/qis/${qi.profile_id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const onPartnerStatus = async (p, status) => {
    try { await api.patch(`/admin/partners/${p.partner_id}`, { status }); toast.success(`${p.firm} ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onPartnerDelete = async (p) => {
    if (!window.confirm(`Delete partner ${p.firm}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/partners/${p.partner_id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14" data-testid="admin-page">
      <span className="eyebrow">Admin</span>
      <h1 className="font-display text-4xl font-black tracking-tight mt-3">Listing approvals</h1>
      <LaunchCard />
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-8">
          <Stat label="Pending" value={stats.pending} testid="admin-stat-pending" />
          <Stat label="Approved" value={stats.approved} testid="admin-stat-approved" />
          <Stat label="Free spots claimed" value={`${stats.spots_claimed}/${stats.spots_total}`} testid="admin-stat-spots" />
          <Stat label="Investor leads" value={stats.leads} testid="admin-stat-leads" />
          <Stat label="Waitlist" value={stats.waitlist} testid="admin-stat-waitlist" />
          <Stat label="Partners pending" value={stats.partners_pending} testid="admin-stat-partners-pending" />
        </div>
      )}
      <div className="flex gap-2 mt-8 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`px-5 py-2.5 rounded-full text-sm font-bold capitalize transition-colors ${tab === t ? "bg-qi-pink text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>{t}</button>
        ))}
      </div>
      <div className="glass-card mt-6 overflow-hidden">
        {rows === null ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-qi-pink" /></div>
          : rows.length === 0 ? <p className="p-10 text-center text-sm text-white/40" data-testid="admin-empty">Nothing here.</p>
            : tab === "waitlist" ? rows.map((w) => (
              <div key={w.email} className="p-5 border-b border-white/5 text-sm" data-testid="admin-waitlist-row"><b>{w.name}</b> · {w.email} {w.company && `· ${w.company}`} <span className="text-white/40 ml-2">{new Date(w.created_at).toLocaleDateString()}</span></div>
            )) : tab === "partners" ? rows.map((p) => <PartnerRow key={p.partner_id} p={p} onStatus={onPartnerStatus} onDelete={onPartnerDelete} />)
              : rows.map((qi) => <QIRow key={qi.profile_id} qi={qi} onStatus={onStatus} onDelete={onDelete} onView={setView} />)}
      </div>
      <Detail qi={view} onClose={() => setView(null)} />
    </div>
  );
}
